/**
 * Paleta "Do livro" — o default da fileira COR do Share Studio redesenhado
 * (§4.5.6 de `post-mechanics-redesign.md`).
 *
 * Regra do mock, literal: *"papel lavado pelo tinto da capa —
 * `mix(paper 88%, corDominante 12%)`"*. O card não fica colorido; ele fica com
 * a TEMPERATURA do livro. 12% é pouco de propósito: a gramática V3 é quieta e
 * a cor entra como tingimento de papel, não como fundo temático.
 *
 * ## Por que isto é barato aqui, e caro em qualquer outro lugar
 *
 * Ler pixel de imagem remota exige canvas **não-tainted**, e capa vem de CDN.
 * `src/utils/imageConverter.ts` já resolve isso: carrega com
 * `crossOrigin="anonymous"`, desenha num canvas e chama `toDataURL()` — e o
 * `toDataURL()` **funcionar** é a prova de que o canvas não está tainted.
 * Como o Studio já consome esses data URLs (via `useShareableImages`), a
 * extração roda sobre um `data:` **same-origin**: sem fetch novo, sem CORS
 * novo, sem proxy novo.
 *
 * ⚠️ Entrada SEM livro (onboarding, wrap) não tem tinto. O contrato devolve
 * `null` e quem chama cai no papel padrão — o mock é explícito: *"entradas sem
 * livro caem no papel padrão"*. Nunca inventar uma cor nesse caso.
 */

import { loadCorsImage } from "../../../utils/loadCorsImage";
import { luminance } from "../../../config/themeContrast";

/** Cor dominante em hex (`#rrggbb`), ou `null` quando não há tinto utilizável. */
export type DominantColor = string | null;

const HEX = (n: number) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, "0");

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${HEX(r)}${HEX(g)}${HEX(b)}`;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/**
 * `mix(base X%, tint (100-X)%)` em sRGB — a operação que o mock especifica.
 *
 * Fica em sRGB de propósito, e não num espaço perceptual: o valor do mock
 * (88/12) foi escolhido olhando o resultado no Figma, que também mistura em
 * sRGB. Trocar o espaço mudaria o resultado visual sem mudar o número.
 */
export function mixHex(baseHex: string, tintHex: string, tintRatio: number): string {
  const base = hexToRgb(baseHex);
  const tint = hexToRgb(tintHex);
  if (!base || !tint) return baseHex;
  const t = clamp(tintRatio, 0, 1);
  return rgbToHex(
    base.r + (tint.r - base.r) * t,
    base.g + (tint.g - base.g) * t,
    base.b + (tint.b - base.b) * t,
  );
}

/** Proporção do tinto no papel. Valor do mock — não ajustar sem o design. */
export const BOOK_TINT_RATIO = 0.12;

/** Papel tingido pela capa. Sem tinto, devolve o papel intocado. */
export function washPaper(paperHex: string, dominant: DominantColor): string {
  if (!dominant) return paperHex;
  return mixHex(paperHex, dominant, BOOK_TINT_RATIO);
}

// ─── Escolha do tinto ────────────────────────────────────────────────────────

/**
 * Descarta pixels que não servem como "tinto do livro":
 *
 * - **quase-branco e quase-preto** — capa tem muito fundo branco e muita
 *   tipografia preta; os dois vencem por volume e não dizem nada sobre o livro;
 * - **cinza** (saturação baixa) — tingir papel com cinza só suja o papel;
 * - **transparente** — capa com alpha (raro, mas o `Adesivo` produz).
 *
 * O resultado é a cor mais frequente **entre as que têm identidade**, que é o
 * que "tinto da capa" quer dizer. Contar todos os pixels devolveria branco em
 * praticamente toda capa.
 */
function isUsable(r: number, g: number, b: number, a: number): boolean {
  if (a < 200) return false;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max > 240 && min > 240) return false; // quase-branco
  if (max < 28) return false; // quase-preto

  // Saturação RELATIVA (`(max-min)/max`), não a diferença absoluta.
  //
  // ⚠️ Este critério já esteve errado: com limiar absoluto (`max - min < 18`)
  // o algoritmo descartava `#273129` — que é o **tinto sálvia do próprio
  // mock**, o exemplo canônico do "Escura: The Name of the Wind". Numa cor
  // escura o spread absoluto entre canais é pequeno mesmo com matiz
  // claramente perceptível (49-39 = 10), enquanto a saturação relativa é
  // 20%. Cinza de verdade fica em ~1%.
  if ((max - min) / max < 0.12) return false;
  return true;
}

interface ColorBucket {
  n: number;
  r: number;
  g: number;
  b: number;
}

/**
 * Particiona os pixels usáveis em baldes por quantização (32 por canal) —
 * grosso o suficiente pra agrupar as variações de uma mesma arte, fino o
 * suficiente pra não fundir duas cores distintas da capa.
 *
 * Miolo COMPARTILHADO por `pickDominantFromPixels` (1 cor, mural/Studio) e
 * `pickDominantPaletteFromPixels` (N cores, F2 do Roadmap PRO — tema da
 * leitura atual): as duas fazem a MESMA pergunta ("quais cores dominam esta
 * imagem?"), só decidem quantas respostas devolver. Extrair evita duas cópias
 * do mesmo loop divergindo no primeiro ajuste de quantização.
 */
function buildColorBuckets(data: Uint8ClampedArray | number[]): ColorBucket[] {
  const buckets = new Map<number, ColorBucket>();

  for (let i = 0; i + 3 < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (!isUsable(r, g, b, a)) continue;

    const key = ((r >> 5) << 10) | ((g >> 5) << 5) | (b >> 5);
    const cur = buckets.get(key);
    if (cur) {
      cur.n += 1;
      cur.r += r;
      cur.g += g;
      cur.b += b;
    } else {
      buckets.set(key, { n: 1, r, g, b });
    }
  }

  return [...buckets.values()];
}

function bucketToHex(v: ColorBucket): string {
  // Média dentro do balde — devolve a cor real da arte, não o centro do
  // bloco de quantização.
  return rgbToHex(v.r / v.n, v.g / v.n, v.b / v.n);
}

/**
 * Cor dominante a partir de pixels RGBA crus.
 *
 * Separado do canvas de propósito: esta é a parte com regra de negócio, e
 * testá-la não deve exigir DOM.
 */
export function pickDominantFromPixels(data: Uint8ClampedArray | number[]): DominantColor {
  const buckets = buildColorBuckets(data);

  let best: ColorBucket | null = null;
  for (const v of buckets) {
    if (!best || v.n > best.n) best = v;
  }
  if (!best) return null;

  return bucketToHex(best);
}

/** Distância euclidiana em RGB — barata, e suficiente pro dedup abaixo. */
function colorDistance(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

/**
 * Teto de proximidade pra duas cores contarem como "a mesma", no dedup de
 * `pickDominantPaletteFromPixels`. 32 é o próprio tamanho do balde de
 * quantização (0-255 em passos de 32) — dois baldes VIZINHOS no espaço de cor
 * não devem virar duas entradas "diferentes" na paleta.
 */
const PALETTE_DEDUP_DISTANCE = 32;

/**
 * As N cores mais dominantes de uma imagem — F2 do Roadmap PRO (tema gerado
 * da capa da leitura atual, `docs/systems/reading-theme.md` §2.1).
 *
 * Função IRMÃ de `pickDominantFromPixels`, não substituta: aquela tem ~15
 * chamadores no Share Studio esperando UMA cor (`string | null`); mudar sua
 * assinatura pra um array quebraria todos em silêncio. Reusa o mesmo miolo de
 * baldes — a mesma pergunta, respondida em profundidade N em vez de 1.
 *
 * Ordena por população (balde mais frequente primeiro) e aplica um dedup
 * guloso por distância: um balde só entra na paleta se estiver longe o
 * bastante de TODOS os já aceitos. Devolve `count` cores, ou menos — capa de
 * baixa diversidade cromática (quase monocromática) tem poucos baldes
 * usáveis pra começar, e é exatamente o sinal que o portão de contraste (F2
 * §4) precisa enxergar, não um bug a esconder.
 */
export function pickDominantPaletteFromPixels(
  data: Uint8ClampedArray | number[],
  count: number,
): string[] {
  const buckets = buildColorBuckets(data).sort((a, b) => b.n - a.n);

  const chosen: ColorBucket[] = [];
  for (const candidate of buckets) {
    if (chosen.length >= count) break;
    const avg = { r: candidate.r / candidate.n, g: candidate.g / candidate.n, b: candidate.b / candidate.n };
    const tooClose = chosen.some((c) => colorDistance(avg, { r: c.r / c.n, g: c.g / c.n, b: c.b / c.n }) < PALETTE_DEDUP_DISTANCE);
    if (!tooClose) chosen.push(candidate);
  }

  return chosen.map(bucketToHex);
}

/**
 * Extrai o tinto de uma imagem já carregável sem CORS (tipicamente o `data:`
 * que `useShareableImages` produziu).
 *
 * Amostra em 32×32: a cor dominante de uma capa não muda com a resolução, e
 * 1024 pixels custam nada — importa porque isto roda no caminho de abertura do
 * Studio, que o BUG-57 já mostrou ser sensível a trabalho na thread principal.
 *
 * Nunca lança: falhar em achar o tinto é degradação (cai no papel padrão), não
 * erro — o card tem que sair de qualquer jeito.
 */
export async function extractDominantColor(src: string): Promise<DominantColor> {
  if (!src || typeof document === "undefined") return null;
  try {
    // Mesmo dono único das capas (`loadCorsImage`): traz o teto por tentativa e
    // a 2ª tentativa em outra chave de cache. Aqui o `src` costuma ser o `data:`
    // que `useShareableImages` produziu — nesse caminho a 1ª tentativa resolve e
    // nada disso é cobrado —, mas quando chega URL remota esta função caía na
    // MESMA colisão de CORS das capas, e o sintoma era mudo: o tinto falhava, o
    // card saía no papel padrão e ninguém sabia por quê (BUG-140).
    const img = await loadCorsImage(src);

    const SIZE = 32;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    // Estoura se o canvas estiver tainted — capturado pelo catch, e o card
    // segue no papel padrão.
    const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
    return pickDominantFromPixels(data);
  } catch {
    return null;
  }
}

/**
 * Luminância MÉDIA de todos os pixels, sem o filtro `isUsable` — F2 (BUG-237).
 *
 * A paleta sozinha não sabe se a capa é escura: `isUsable` joga fora o
 * quase-preto e o quase-branco ANTES dos baldes, então uma capa preta com um
 * detalhe vermelho chega à paleta como "vermelho". Quem decide se o tema
 * gerado nasce claro ou escuro precisa da capa inteira, e é isto que mede.
 * Mesma fórmula de luminância de `themeContrast.ts` (WCAG), não uma 2ª.
 */
export function meanLuminanceFromPixels(data: Uint8ClampedArray | number[]): number | null {
  let sum = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 200) continue;
    sum += luminance([data[i], data[i + 1], data[i + 2], 1]);
    n += 1;
  }
  return n === 0 ? null : sum / n;
}

/** O que `extractCoverPalette` devolve quando a capa PÔDE ser lida. */
export interface CoverPalette {
  /** N cores dominantes, em ordem de dominância — pode ser `[]` (capa sem cor). */
  palette: string[];
  /** Luminância média da capa inteira (0–1), ou `null` se todo pixel era transparente. */
  meanLuminance: number | null;
}

/**
 * Paleta + luminância média de uma capa, distinguindo "não li a capa" de
 * "a capa não tem cor" — F2 (BUG-238).
 *
 * `extractDominantPalette` devolve `[]` nos dois casos, e o hook do tema da
 * leitura traduzia os dois como "essa capa não rendeu um tema legível". Era
 * diagnóstico falso: capa hospedada sem CORS (Google Books, ISBNdb) contamina
 * o canvas, `getImageData` lança, e a pessoa lia que a CAPA era o problema.
 * Aqui `null` = a imagem não pôde ser lida (rede, CORS, timeout); um objeto
 * com `palette: []` = leu, e não há cor com identidade.
 *
 * ⚠️ O `src` precisa ser carregável com `crossOrigin` — quem chama passa
 * pelo proxy (`proxifyImageUrl`), como o Studio faz em `getOptimizedBase64`.
 */
export async function extractCoverPalette(src: string, count: number): Promise<CoverPalette | null> {
  if (!src || typeof document === "undefined") return null;
  try {
    const img = await loadCorsImage(src);

    const SIZE = 32;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
    return {
      palette: pickDominantPaletteFromPixels(data, count),
      meanLuminance: meanLuminanceFromPixels(data),
    };
  } catch {
    return null;
  }
}

/**
 * As N cores dominantes de uma capa carregável sem CORS — F2 do Roadmap PRO.
 * Função IRMÃ de `extractDominantColor`: mesmo carregamento, mesma amostra
 * 32×32 (a paleta de uma capa não muda com a resolução da amostra), mesmo
 * "nunca lança" — falhar em extrair é degradação (o chamador decide o
 * fallback), não erro.
 */
export async function extractDominantPalette(src: string, count: number): Promise<string[]> {
  if (!src || typeof document === "undefined") return [];
  try {
    const img = await loadCorsImage(src);

    const SIZE = 32;
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return [];
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
    return pickDominantPaletteFromPixels(data, count);
  } catch {
    return [];
  }
}
